import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { Box, Divider, OutlinedInput, Typography } from '@mui/material'

import { AlertDialog, FormLabel, MButton, ProgressLoaders } from 'components'
import { isFeatureActive } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMobileBreakpoints } from 'utils'

export const AccountDetails = (): JSX.Element => {
  const isMobile = useMobileBreakpoints()
  const { account, updateMailingAddress, getUser } = useAuth()
  const [editing, setEditing] = useState<boolean>(false)
  const [dialogData, setDialogData] = useState<any>({
    type: 'success',
    isOpen: false,
    title: 'Changes Saved',
    message: 'You have successfully update the customer’s account details.'
  })
  const isInvoiceCloudEnabled = isFeatureActive('ic_enabled')
  const allowEditAccount = isFeatureActive('edit_account_details')

  const { handleSubmit, control, reset, formState } = useForm<{
    mailingAddress: string
    mailingAddress2: string
    city: string
    state: string
    postal: string
  }>({
    mode: 'all'
  })

  useEffect(() => {
    if (account) {
      reset({
        mailingAddress: account?.mailingAddress?.addressLine1,
        mailingAddress2: account?.mailingAddress?.addressLine2,
        city: account?.mailingAddress?.city,
        state: account?.mailingAddress?.state,
        postal: account?.mailingAddress?.postal
      })
    }
  }, [account, reset])

  const onSubmit = async (data: any): Promise<void> => {
    const { accountId, ccsEmail } = account
    const { mailingAddress, mailingAddress2, city, state, postal } = data

    if (editing) {
      const defaultPayload = {
        accountId,
        email: ccsEmail
      }
      const mailingAddressPayload = {
        ...defaultPayload,
        addressLine1: mailingAddress,
        addressLine2: mailingAddress2,
        city,
        state,
        postal
      }

      try {
        await updateMailingAddress(mailingAddressPayload)
        await getUser('', ccsEmail)
        setDialogData({ ...dialogData, isOpen: true })
      } catch (error) {
        await getUser('', ccsEmail)
        setDialogData({
          type: 'error',
          isOpen: true,
          title: 'An error occured',
          message: 'There is an error updating the customer\'s account details.'
        })
      }
    }
    setEditing(!editing)
  }

  if (!account) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height="100%" />
      </Box>
    )
  }

  return (
    <Box border={`1px solid ${color.secondary.dark[300]}`}>
      <form method="post" onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          p={1}
          bgcolor={color.grey[50]}
          gap={isMobile ? 1 : 0}
        >
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <AccountCircleIcon
              sx={{
                color: color.grey[500],
                backgroundColor: color.grey[200],
                fontSize: 26,
                borderRadius: '50%',
                padding: 1
              }}
            />

            <Typography color={color.black} fontWeight={600} fontSize={16}>
              Account Details
            </Typography>
          </Box>

          <Box
            display={isInvoiceCloudEnabled ? 'flex' : 'none'}
            flexDirection={isMobile ? 'column' : 'row'}
            gap={1}
          >
            {allowEditAccount && <MButton
              type="submit"
              variant="outlined"
              rounded="true"
              texttransform="none"
              fullWidth={isMobile}
              disabled={!account || formState.isSubmitting}

            >
              {editing ? 'Save Changes' : 'Edit Information'}
            </MButton>}

            {editing && (
              <MButton
                type="button"
                variant="outlined"
                rounded="true"
                texttransform="none"
                fullWidth={isMobile}
                onClick={() => {
                  setEditing(!editing)
                }}
              >
                Cancel
              </MButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ color: color.secondary.dark[300] }} />

        <Box
          display="flex"
          justifyContent="space-around"
          flexDirection={isMobile ? 'column' : 'row'}
          px={2}
          py={1.5}
          bgcolor={color.white}
          gap={isMobile ? 1 : 2}
        >
          <Box display="flex" flexDirection="column" width="100%">
            <FormLabel textcolor={color.grey[600]}>Street Address #1</FormLabel>
            <Controller
              name="mailingAddress"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  onChange={onChange}
                  value={value}
                  type="text"
                  disabled={!editing}
                />
              )}
            />
          </Box>

          <Box display="flex" flexDirection="column" width="100%">
            <FormLabel textcolor={color.grey[600]}>Street Address #2</FormLabel>
            <Controller
              name="mailingAddress2"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  onChange={onChange}
                  value={value}
                  type="text"
                  disabled={!editing}
                />
              )}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-around"
          flexDirection={isMobile ? 'column' : 'row'}
          px={2}
          py={1.5}
          bgcolor={color.white}
          gap={isMobile ? 1 : 2}
        >
          <Box display="flex" flexDirection="column" width="100%">
            <FormLabel textcolor={color.grey[600]}>City</FormLabel>
            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  onChange={onChange}
                  value={value}
                  type="text"
                  disabled={!editing}
                />
              )}
            />
          </Box>

          <Box display="flex" flexDirection="column" width="100%">
            <FormLabel textcolor={color.grey[600]}>State</FormLabel>
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  onChange={onChange}
                  value={value}
                  type="text"
                  disabled={!editing}
                />
              )}
            />
          </Box>

          <Box display="flex" flexDirection="column" width="100%">
            <FormLabel textcolor={color.grey[600]}>Zip code</FormLabel>
            <Controller
              name="postal"
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <OutlinedInput
                  name={name}
                  onChange={onChange}
                  value={value}
                  type="text"
                  disabled={!editing}
                />
              )}
            />
          </Box>
        </Box>
      </form>

      <AlertDialog
        open={dialogData.isOpen}
        type={dialogData.type}
        title={dialogData.title}
        message={dialogData.message}
        handleClick={() => {
          setDialogData({ ...dialogData, isOpen: false })
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <MButton
            variant="contained"
            type="button"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setDialogData({ ...dialogData, isOpen: false })
            }}
          >
            Go back
          </MButton>
        </Box>
      </AlertDialog>
    </Box>
  )
}
